<template>
  <v-container fluid>
    <v-row justify="end">
      <v-col cols="12" align="center" justify="center">
        <v-sheet>
          <v-row>
            <v-col cols="9">
              <h1>Club Onboarding</h1>
            </v-col>
            <v-col cols="3">
              <v-select v-model="role" :items="roleOptions" label="Get my clients for role"> </v-select>
            </v-col>
          </v-row>
          <v-data-table
            :headers="table.headers"
            :options="table.options"
            :items="clients"
            :search="search"
            :show-group-by="true"
            :hide-default-footer="true"
            fixed-header
            height="750"
            disable-pagination
            dense
            :loading="loading"
          >
            <template v-slot:top>
              <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
                <template v-slot:label>
                  <v-icon color="secondary"> mdi-magnify </v-icon>
                  Search
                </template>
              </v-text-field>
            </template>

            <template v-slot:[`item.controls`]="props" width="100">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    elevation="13"
                    color="secondary"
                    class="ma-1 accent lighten-5"
                    dark
                    v-bind="attrs"
                    v-on="on"
                    @click="openNotes(props.item.client.id)"
                  >
                    <v-icon>mdi-notebook</v-icon>
                  </v-btn>
                </template>
                <span>Notes</span>
              </v-tooltip>
              <v-btn
                icon
                elevation="13"
                color="secondary"
                class="ma-1 accent lighten-5"
                dark
                @click="openCalendar(props.item.userId)"
              >
                <v-icon>mdi-calendar</v-icon>
              </v-btn>
            </template>

            <template v-slot:[`item.hasActivity`]="props">
              <v-icon v-if="props.item.hasActivity" color="complete-green">check_circle</v-icon>
              <v-icon v-else color="non-compliant">mdi-close</v-icon>
            </template>

            <template v-slot:[`item.welcomeMessage`]="props">
              <v-simple-checkbox
                color="secondary"
                class="mt-0"
                :value="props.item.welcomeMessage"
                :disabled="loading"
                v-ripple
                @input="checkOffStep(props.item.id, 'WELCOME_MESSAGE_SENT')"
              />
            </template>

            <template v-slot:[`item.clientEngaged`]="props">
              <v-simple-checkbox
                color="secondary"
                class="mt-0"
                v-ripple
                :value="props.item.clientEngaged"
                :disabled="loading"
                @input="checkOffStep(props.item.id, 'ENGAGED_WITH_CM')"
              />
            </template>

            <template v-slot:[`item.clientFullName`]="props">
              <div :class="getAgeingColor(props.item) + ' pa-1'">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <a
                      :href="props.item.profileLink"
                      target="_blank"
                      class="secondary--text text-body-1 text-decoration-none"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ props.item.clientFullName }}
                      <br />
                      Phone: {{ props.item.phone }}
                    </a>
                  </template>
                  <span>Last Login: {{ props.item.client.lastLogin | moment("YYYY-MM-DD HH:MM") }}</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:[`item.product`]="props">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ props.item.product }}
                  </span>
                </template>
                <span v-for="email in props.item.emailComms" :key="email.id" class="d-block">
                  {{ email.sgTemplateName }}: {{ email.event }}
                </span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.supportFullName`]="props">
              <v-autocomplete
                v-if="editCell == 'support-' + props.item.id"
                :value="props.item.support && props.item.support.id"
                dense
                :items="staff"
                @change="updateSupportUser($event, props.item.id)"
              ></v-autocomplete>
              <span v-else @click="editCell = 'support-' + props.item.id" class="fake-link">
                {{ props.item.supportFullName }}
              </span>
            </template>

            <template v-slot:[`item.onboardingCompleted`]="props">
              <v-simple-checkbox
                color="secondary"
                class="mt-0"
                v-ripple
                :value="props.item.onboardingCompleted"
                :disabled="loading"
                @input="updateOnboardingCompleted($event, props.item)"
              />
            </template>
          </v-data-table>
          <v-dialog v-model="modals.notes" class="limited-height" width="900" transition="dialog-bottom-transition">
            <user-notes
              v-if="modals.notes"
              :userId="selectedUserId"
              :coachId="currentUser && currentUser.id"
              @problem="emitProblem()"
              @close="closeNotes"
            />
          </v-dialog>
          <v-dialog v-model="modals.calendar" class="limited-height" width="900" transition="dialog-bottom-transition">
            <v-card>
              <calendar v-if="modals.calendar" :userId="selectedUserId"></calendar>
            </v-card>
          </v-dialog>
        </v-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { CLUB_ONBOARDING_QUERY, UPSERT_SUBSCRIPTION_CHECK_MUTATION } from "@/constants/graphql/operations/onboarding";
import { UPDATE_SUBSCRIPTION_MUTATION } from "@/constants/graphql/users/subscription";
import { STAFF_QUERY } from "@/constants/graphql/users/user";
import { getUserName } from "@/lib/username";
import UserNotes from "@/components/dashboard/components/notes/UserNotes";
import Calendar from "@/components/dashboard/components/Calendar";

export default {
  name: "OperationsOnboarding",
  components: {
    UserNotes,
    Calendar,
  },
  data() {
    return {
      clients: [],
      search: "",
      modals: {
        notes: false,
        calendar: false,
      },
      role: null,
      roleOptions: [
        { value: null, text: "" },
        { value: "support", text: "Owner" },
      ],
      editCell: null,
      staff: [],
      loading: true,
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "Sign up date", value: "signUpDate", groupable: false },
          { text: "Days since sign up", value: "daysSinceSignUpDate", groupable: false },
          { text: "Client", value: "clientFullName", groupable: false },
          {
            text: "Previous Subscription",
            value: "previousProduct",
            groupable: false,
          },
          {
            text: "",
            value: "controls",
            sortable: false,
            groupable: false,
            filterable: false,
            width: 20,
          },
          { text: "Owner", value: "supportFullName", groupable: false },
          {
            text: "Welcome Message Sent",
            value: "welcomeMessage",
            groupable: false,
          },
          {
            text: "Enaged with CM",
            value: "clientEngaged",
            groupable: false,
          },
          { text: "Workout Block", value: "hasWorkoutBlock", groupable: false },
          { text: "Has Nutrition Plan", value: "hasNutritionPlan", groupable: false },
          { text: "Has course Block", value: "hasCourseBlock", groupable: false },
          { text: "Activity logged", value: "hasActivity", groupable: false },
          {
            text: "Complete onboarding",
            value: "onboardingCompleted",
            groupable: false,
          },
        ],
      },
    };
  },
  computed: {
    currentUser() {
      return JSON.parse(localStorage.getItem("user")) || store.getters.user;
    },
  },
  methods: {
    getAgeingColor(item) {
      var diff = this.$moment().diff(this.$moment(item.signUpDate), "days");

      if (diff > 7) {
        return "metric-red";
      }
      if (diff > 3 && diff < 8) {
        return "metric-yellow";
      }
      return "metric-green";
    },
    openCalendar(userId) {
      this.selectedUserId = userId;
      this.modals.calendar = true;
    },
    closeCalendar() {
      this.modals.calendar = false;
    },
    openNotes(userId) {
      this.selectedUserId = userId;
      this.modals.notes = true;
    },
    closeNotes() {
      this.modals.notes = false;
    },
    checkOffStep(subscriptionId, type) {
      this.$apollo
        .mutate({
          mutation: UPSERT_SUBSCRIPTION_CHECK_MUTATION,
          variables: {
            input: {
              subscriptionId: subscriptionId,
              type: type.toLowerCase(),
            },
          },
          refetchQueries: ["userSubscriptions"],
        })
        .then((result) => {
          if (!result.data.upsertSubscriptionCheckRecord.success) {
            alert(result.data.upsertSubscriptionCheckRecord.error);
          } else {
            this.$toasted.success("Success", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    updateSupportUser(event, userSubscriptionId) {
      this.mutateSubscription({
        id: userSubscriptionId,
        supportUserId: event,
      });
    },
    updateOnboardingCompleted(event, item) {
      let userSubscriptionId = item.id;
      this.loading = true;
      item.onboardingCompleted = true;
      this.mutateSubscription({
        id: userSubscriptionId,
        onboardingCompleted: event,
      });
    },
    mutateSubscription(input) {
      this.$apollo
        .mutate({
          mutation: UPDATE_SUBSCRIPTION_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["userSubscriptions"],
        })
        .then((result) => {
          if (!result.data.updateSubscriptionRecord.updated) {
            alert(result.data.updateSubscriptionRecord.error);
          } else {
            this.$toasted.success("Updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
  },
  apollo: {
    userSubscriptions: {
      query: CLUB_ONBOARDING_QUERY,
      variables() {
        this.loading = true;
        let input = {
          stage: "onboarding",
          productType: "club",
        };
        if (this.$route.query.role || this.role) {
          let role = this.role || this.$route.query.role;
          this.role = role;
          let userId = this.$route.query.userId || this.currentUser.id;
          let encoded_json_payload = btoa("UserNode:" + userId);

          switch (role) {
            case "support":
              input["supportOwnerId"] = encoded_json_payload;
              break;
          }
        }
        return input;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.clients = [];

          this.clients = response.data.userSubscriptions.edges.map((item) => {
            return {
              id: this.getId(item.node.id),
              client: item.node.user,
              clientFullName: getUserName(item.node.user),
              support: item.node.supportOwner,
              supportFullName: item.node.supportOwner ? getUserName(item.node.supportOwner) : "N/S",
              profileLink: `/clients/${item.node.user.id}`,
              signUpDate: this.$moment(item.node.startDate).format("YYYY-MM-DD"),
              daysSinceSignUpDate: this.$moment().diff(this.$moment(item.node.startDate), "days"),
              programStartDate: item.node.programStartDate,
              onboardingCompleted: item.node.onboardingCompleted,
              phone: item.node.user.userProfile.phone,
              previousProduct: item.node.previousProduct && item.node.previousProduct.name,
              welcomeMessage:
                item.node.subscriptionProcessChecks.filter((check) => check.type == "WELCOME_MESSAGE_SENT").length > 0,
              clientEngaged:
                item.node.subscriptionProcessChecks.filter((check) => check.type == "ENGAGED_WITH_CM").length > 0,
              hasActivity: item.node.user.useractivitySet.edges && item.node.user.useractivitySet.edges.length > 0,
              hasNutritionPlan: item.node.user.subscriptionnutritionblockSet.edges.length > 0 ? "Yes" : "No",
              hasWorkoutBlock:
                item.node.user.subscriptionblockSet.filter(
                  (block) =>
                    this.$moment(item.node.startDate) < this.$moment(block.startDate) &&
                    (block.block.type == "PROGRAM" || block.block.type == "CHALLENGE")
                ).length > 0
                  ? "Yes"
                  : "No",
              hasCourseBlock:
                item.node.user.subscriptionblockSet.filter(
                  (block) =>
                    this.$moment(item.node.startDate) < this.$moment(block.startDate) && block.block.type == "COURSE"
                ).length > 0
                  ? "Yes"
                  : "No",
              emailComms: item.node.userEmailComms.filter(
                (log) => this.$moment(log.createdAt) >= this.$moment(item.node.startDate)
              ),
            };
          });

          this.loading = false;
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
    users: {
      query: STAFF_QUERY,
      variables() {
        return {
          isActive: true,
          isStaff: true,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.staff = [];

          response.data.users.edges.forEach((item) => {
            let data = {
              value: this.getId(item.node.id),
              text: getUserName(item.node),
            };

            this.staff.push(data);
          });

          this.staff.sort((a, b) => (a.text > b.text ? 1 : -1));
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>

<style scoped>
.fake-link {
  cursor: pointer;
}
</style>
